import clsx from 'clsx';
import type React from 'react';
import { type PressHookProps, usePress } from 'react-aria';

import { Tooltip } from '../Tooltip';
import { type IconDefinition, renderIcon } from '../util';
import styles from './Link.module.css';

export type LinkKind = 'default' | 'inherit' | 'upgrade' | 'table';

/** Schemes that run script rather than navigate. A link's target can come from user data. */
const SCRIPT_SCHEMES = ['javascript:', 'data:', 'vbscript:'];

/** Whether a target would execute instead of navigating, so it must not reach `window.location`. */
function isScriptURL(location: string): boolean {
    // Browsers strip whitespace and control characters before reading the scheme, so `java\tscript:`
    // runs just as well as `javascript:`.
    const scheme = location.replace(/[\u0000-\u0020\u007f-\u00a0]/g, '').toLowerCase();
    return SCRIPT_SCHEMES.some((unsafe) => scheme.startsWith(unsafe));
}

export type LinkProps<NavigationObj extends object = Record<string, unknown>> = {
    /**
     * Set the link to a non-interactive state.
     */
    disabled?: boolean;
    /**
     * Force a location change with page reload.
     */
    forceHardNavigation?: boolean;
    /**
     * The visual variant of the link.
     * @default 'default'
     */
    kind?: LinkKind;
    /**
     * You can pass the link's text either as a string label prop
     * OR as a child when embedded within text.
     */
    label?: string;
    /**
     * Icon positioned ahead of the label.
     */
    leadingIcon?: IconDefinition | undefined;
    /**
     * An optional callback triggered when navigating to
     * allow hooking into a router. Without it this link
     * will act as a standard browser anchor. With it and
     * when the navigation is in the same target then the
     * function can be used to trigger a `navigate` action.
     */
    navigate?: (to: NavigationObj) => void;
    /**
     * Callback triggered when pressing the link.
     * Doesn't affect navigation but can be used for
     * analytics events and additional actions.
     */
    onPress?: Awaited<PressHookProps['onPress']>;
    /**
     * A navigation target object that can be transformed
     * into a url and passed as parameter to `navigateFn`
     */
    to?: NavigationObj;
    /**
     * Icon positioned after the label.
     */
    trailingIcon?: IconDefinition;
    /**
     * A transformer that works with the `to` prop to
     * generate a url for the link
     */
    transformToURL?: (to: NavigationObj) => string;

    className?: string;
} & React.AnchorHTMLAttributes<HTMLAnchorElement>;

/**
 *
 * @example
 * ```
 *  import { Link } from '@gitbook/spine-react';
 *
 *  // as a simple link using browser default behaviour and <a> props
 *  <Link href="#" target="_blank">
 *      Blank target link
 *  </Link>
 *
 *  // using a custom navigation
 *  <Link
 *      to={{ screen: { type: 'space', space } }}
 *      navigate={(to) => navigate(to)}
 *      transformToURL={getNavigationURL}>Router Link</Link>
 */
export function Link<NavObj extends object>(props: LinkProps<NavObj>) {
    const {
        children,
        disabled = false,
        forceHardNavigation = false,
        href,
        kind = 'default',
        label,
        leadingIcon,
        navigate,
        onPress,
        className,
        title,
        to,
        trailingIcon,
        transformToURL,
        ...rest
    } = props;
    const requested = to && transformToURL ? transformToURL(to) : href;
    // A script URL is dropped rather than rendered inert, so the anchor stays plain text: the
    // click handler navigates through `window.location`, which React cannot guard.
    const location = requested && isScriptURL(requested) ? undefined : requested;
    // Unlike buttons, a link cannot be disabled in a browser.
    // To mimic disabling the link we will remove the href and
    // set aria properties to announce "Link, disabled".
    const interactiveProps = disabled
        ? {
              href: undefined,
              role: 'link',
              'aria-disabled': true,
          }
        : { href: location };

    const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
        if (e.isDefaultPrevented() || !interactiveProps.href) {
            return;
        }

        // if forceHardNavigation, let the browser handle navigation
        if (forceHardNavigation) {
            return;
        }

        // if the target is something other than the same window,
        // let the browser handle it
        if (props.target && props.target !== '_self') {
            return;
        }

        // if shift or ctrl are pressed, use the
        // default browser behaviour and open the link in a new tab.
        if (e.metaKey || e.ctrlKey) {
            return;
        }

        e.preventDefault();

        // if there's a navigate function use that
        if (navigate && to) {
            return navigate(to);
        }

        // otherwise just set the window to the new location to navigate
        window.location.href = interactiveProps.href;
    };

    const { pressProps } = usePress({
        onPress,
        onClick: handleClick,
        isDisabled: disabled,
    });

    const pressable = !disabled && onPress;
    const interactive = location || pressable;
    return (
        <Tooltip label={title} disabled={!title}>
            <a
                {...rest}
                {...interactiveProps}
                {...pressProps}
                className={clsx(
                    'cursor-pointer',
                    styles.link,
                    styles[kind],
                    {
                        [styles.placeholder]: disabled || !interactive,
                        'px-2.5': kind === 'upgrade',
                    },
                    className
                )}
            >
                {leadingIcon ? renderIcon(leadingIcon, { className: 'shrink-0 mr-[0.5em]' }) : null}
                {label ?? children}
                {trailingIcon
                    ? renderIcon(trailingIcon, { className: 'shrink-0 ml-[0.5em]' })
                    : null}
            </a>
        </Tooltip>
    );
}
